import React, {useCallback, useMemo, useRef, useState} from "react";
import {Badge, Button, Col, DatePicker, Form, Input, notification, Row, Select, Space, Tooltip, Upload} from "antd";
import {ReloadOutlined, SearchOutlined, UploadOutlined} from "@ant-design/icons";
import {AgGridReact} from "ag-grid-react";
import {useIntl, type IntlShape} from "react-intl";
import dayjs from "dayjs";
import {useApolloClient} from "@apollo/client";
import {getAgGridLocalization} from "../../../../info-portal/components/grid/GridUtil";
import {getRangePickerLocalization} from "../../../../info-portal/components/grid/CalendarUtils";
import {getI18n, isExternalUser} from "../../../../../utils/Utils";
import * as Types from "../../../../../__generated__/types";
import {useSendUploadedDocumentsToFavorCockpitMutation} from "../mutations.generated";
import {useGetUploadDocumentsForBorderoLazyQuery} from "../queries.generated";
import {PartnerLookup} from "../../../../../components/PartnerLookup";
import styles from "../../../../info-portal/components/styling/infoportal.module.css";
import moduleStyles from "../../document.module.css";
import {useFreightGridToolbar} from "../../hooks/useFreightGridToolbar";
import FreightGridToolbar from "../../components/FreightGridToolbar";
import FreightWorkspaceShell from "../../components/FreightWorkspaceShell";
import {
    getDocumentUserEntitlementResultByUserId,
} from "../../../../../main/__generated__/getDocumentUserEntitlementResultByUserId";
import {
    BeleganforderungFilter,
    BeleganforderungProps,
    BeleganforderungRow,
    buildColumnDefs,
    createRowKey,
    FreightEntitlement,
    mapFilesByBulkUploadKey,
    notifyBulkUploadSummary,
    notifyUploadFailure,
    onlyShowFreightPartners,
    uploadDocumentForRow,
} from "./beleganforderungHelpers";
import { mapGetUploadDocumentsForBorderoRows } from "../adapters/favorAdapters";
import { notifyFreightAuthOrGenericError } from "../freightFavorErrorHandling";
import { DOCUMENT_USER_ENTITLEMENT_RESULT_BY_USER_ID } from "../../../../../main/queries";
import {
    getFreightDateBounds,
    getFreightDefaultDateRange,
    isFreightDateOutOfRange,
} from "../freightDateRange";

const STATUS_OPTIONS: Types.BorderoEdpStatus[] = [
    Types.BorderoEdpStatus.DocumentRequested,
    Types.BorderoEdpStatus.DocumentReceived,
    Types.BorderoEdpStatus.InProgress,
    Types.BorderoEdpStatus.InCorrection,
    Types.BorderoEdpStatus.InSettlement,
    Types.BorderoEdpStatus.Billed,
    Types.BorderoEdpStatus.Ok,
];

function getStatusOptionLabel(status: Types.BorderoEdpStatus, intl: IntlShape): string {
    switch (status) {
        case Types.BorderoEdpStatus.DocumentRequested:
            return getI18n("freight-status-document-requested", "Document Requested", intl);
        case Types.BorderoEdpStatus.DocumentReceived:
            return getI18n("freight-status-document-received", "Document Received", intl);
        case Types.BorderoEdpStatus.InProgress:
            return getI18n("freight-status-in-progress", "In Progress", intl);
        case Types.BorderoEdpStatus.InCorrection:
            return getI18n("freight-status-in-correction", "In Correction", intl);
        case Types.BorderoEdpStatus.InSettlement:
            return getI18n("freight-status-in-settlement", "In Settlement", intl);
        case Types.BorderoEdpStatus.Billed:
            return getI18n("freight-status-billed", "Billed", intl);
        case Types.BorderoEdpStatus.Ok:
            return getI18n("freight-status-ok", "OK", intl);
        default:
            return status;
    }
}

const filterRowsBySearch = (
    sourceRows: BeleganforderungRow[],
    filters: BeleganforderungFilter,
): BeleganforderungRow[] => {
    const borderoNumberFilter = filters.borderoNumber?.trim().toLowerCase();
    const selectedStatuses = filters.status ?? [];

    return sourceRows.filter((row) => {
        const rowStatus = row.status as Types.BorderoEdpStatus | undefined;
        const matchesBorderoNumber = !borderoNumberFilter || row.borderoNumber.toLowerCase().includes(borderoNumberFilter);
        const matchesStatus = !selectedStatuses.length || (rowStatus ? selectedStatuses.includes(rowStatus) : false);
        return matchesBorderoNumber && matchesStatus;
    });
};

const BeleganforderungComponent: React.FC<BeleganforderungProps> = ({ currentUser, distinctEntitlements }) => {
    const intl = useIntl();
    const selectedLanguage = localStorage.getItem("locale") || "en";
    const { minDate, maxDate } = useMemo(() => getFreightDateBounds(), []);
    const isInternalUser = !isExternalUser(distinctEntitlements);
    const [form] = Form.useForm<BeleganforderungFilter>();
    const [rows, setRows] = useState<BeleganforderungRow[]>([]);
    const defaultDateRange = useMemo<BeleganforderungFilter>(() => getFreightDefaultDateRange(), []);
    const partnerRules = useMemo(
        () => (isInternalUser ? [{
            required: true,
            message: getI18n(
                "freight-partner-required-internal",
                "Please select a partner before searching.",
                intl,
            ),
        }] : undefined),
        [intl, isInternalUser],
    );
    const [uploadingRowKey, setUploadingRowKey] = useState<string | null>(null);
    const [isBulkUploading, setIsBulkUploading] = useState(false);
    const pendingBulkUploadFilesRef = useRef<File[]>([]);
    const bulkUploadTimerRef = useRef<number | null>(null);

    const [loadDocuments, {loading}] = useGetUploadDocumentsForBorderoLazyQuery({
        fetchPolicy: "no-cache",
        context: {
            skip401Logout: true,
        },
        onCompleted: (data) => {
            const nextRows = mapGetUploadDocumentsForBorderoRows(data.getUploadDocumentsForBordero);
            const currentValues = form.getFieldsValue();
            const filteredRows = filterRowsBySearch(nextRows, currentValues);

            setRows(filteredRows);
        },
        onError: (error) => {
            notifyFreightAuthOrGenericError({
                error,
                intl,
                unauthorizedMessage: {
                    id: "freight-beleganforderung-load-unauthorized",
                    defaultMessage: "You are not authorized to load documents.",
                },
                genericMessage: {
                    id: "freight-beleganforderung-load-error",
                    defaultMessage: "Could not load documents.",
                },
            });
        },
    });
    const [sendUploadedDocumentsToFavorCockpitMutation] = useSendUploadedDocumentsToFavorCockpitMutation();
    const apolloClient = useApolloClient();

    const resolveCarrierNumberFilter = async (selectedPartnerNo?: string): Promise<string | undefined> => {
        const selectedCarrierNumber = selectedPartnerNo?.trim();

        if (selectedCarrierNumber) {
            return selectedCarrierNumber;
        }

        if (!isExternalUser(distinctEntitlements) || !currentUser?.uid) {
            return undefined;
        }

        try {
            const response = await apolloClient.query<getDocumentUserEntitlementResultByUserId>({
                query: DOCUMENT_USER_ENTITLEMENT_RESULT_BY_USER_ID,
                variables: {
                    userId: currentUser.uid,
                    entitlement: FreightEntitlement,
                },
                fetchPolicy: "network-only",
            });
            const entitledPartnerNumbers = Array.from(
                new Set(
                    (response.data?.getDocumentUserEntitlementResultByUserId?.partners || [])
                        .map((partner) => partner?.partnerNo?.trim())
                        .filter((partnerNo): partnerNo is string => Boolean(partnerNo)),
                ),
            );

            return entitledPartnerNumbers.length ? entitledPartnerNumbers.join(",") : undefined;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    };

    const onSearch = async (values: BeleganforderungFilter) => {
        const carrierNumber = await resolveCarrierNumberFilter(values.partnerNo);
        try {
            await loadDocuments({
                variables: {
                    request: {
                        carrierNumber,
                        borderoNumber: values.borderoNumber?.trim() || undefined,
                        dateFrom: values.dateFrom?.format("YYYY-MM-DD") || undefined,
                        dateUntil: values.dateUntil?.format("YYYY-MM-DD") || undefined,
                        status: values.status?.length ? values.status : undefined,
                    },
                },
            });
        } catch (error) {
            notifyFreightAuthOrGenericError({
                error,
                intl,
                unauthorizedMessage: {
                    id: "freight-beleganforderung-search-unauthorized",
                    defaultMessage: "You are not authorized to load documents.",
                },
                genericMessage: {
                    id: "freight-beleganforderung-search-error",
                    defaultMessage: "Could not load documents.",
                },
            });
        }
    };

    const onReset = () => {
        form.resetFields();
        setRows([]);
    };


    const onUploadSelected = useCallback(async (files: File[]) => {
        if (!files.length) {
            return;
        }

        const { filesByKey, invalidFileNames, duplicateMatches } = mapFilesByBulkUploadKey(files);

        setIsBulkUploading(true);
        const borderoCounts: Record<string, { successCount: number; failedCount: number }> = {};

        try {
            const uploadPlans = Array.from(filesByKey.values()).map(({ file, metadata }) => {
                const borderoNumber = metadata.borderoNumber;

                if (!borderoCounts[borderoNumber]) {
                    borderoCounts[borderoNumber] = { successCount: 0, failedCount: 0 };
                }

                const uploadRow: BeleganforderungRow = {
                    carrierNumber: metadata.carrierNumber,
                    borderoNumber,
                    borderoPosition: metadata.borderoPosition,
                    borderoDate: "",
                };

                return {
                    borderoNumber,
                    uploadPromise: uploadDocumentForRow(file, uploadRow, sendUploadedDocumentsToFavorCockpitMutation),
                };
            });

            const settledUploads = await Promise.allSettled(uploadPlans.map((plan) => plan.uploadPromise));
            settledUploads.forEach((result, index) => {
                const borderoNumber = uploadPlans[index].borderoNumber;

                if (result.status === "fulfilled") {
                    borderoCounts[borderoNumber].successCount += 1;
                } else {
                    borderoCounts[borderoNumber].failedCount += 1;
                }
            });

            const borderoSummary = Object.keys(borderoCounts).map((borderoNumber) => ({
                borderoNumber,
                successCount: borderoCounts[borderoNumber].successCount,
                failedCount: borderoCounts[borderoNumber].failedCount,
            }));

            notifyBulkUploadSummary(intl, borderoSummary, invalidFileNames, duplicateMatches);
        } finally {
            setIsBulkUploading(false);
        }
    }, [intl, sendUploadedDocumentsToFavorCockpitMutation]);

    const handleDocumentUpload = useCallback(async (file: File, row: BeleganforderungRow) => {
        const rowKey = createRowKey(row);
        setUploadingRowKey(rowKey);

        try {
            await uploadDocumentForRow(file, row, sendUploadedDocumentsToFavorCockpitMutation);

            notification.success({
                message: getI18n("freight-beleganforderung-upload-success", "Document uploaded successfully.", intl),
                description: row.borderoNumber,
            });
        } catch (error) {
            notifyUploadFailure(intl, error, row.borderoNumber);
        } finally {
            setUploadingRowKey(null);
        }
    }, [intl, sendUploadedDocumentsToFavorCockpitMutation]);

    const columnDefs = useMemo(
        () => buildColumnDefs(intl, handleDocumentUpload, uploadingRowKey),
        [handleDocumentUpload, intl, uploadingRowKey],
    );

    const { menuItems, onExport, onGridReady: hookOnGridReady, onResetView } = useFreightGridToolbar<BeleganforderungRow>({
        columnDefs,
        exportWarningTitle: getI18n("freight-beleganforderung-export", "Export", intl),
        intl,
        storageKey: "freight-beleganforderung_ag_grid_personalized_view",
    });

    return (
        <FreightWorkspaceShell
            searchForm={
                <Form form={form} layout="vertical" onFinish={onSearch} initialValues={defaultDateRange}>
                    <Row gutter={16}>
                        <PartnerLookup
                            intl={intl}
                            entitlement={FreightEntitlement}
                            onChange={() => undefined}
                            currentUser={currentUser}
                            distinctEntitlements={distinctEntitlements}
                            filter={onlyShowFreightPartners}
                            colSpan={6}
                            rules={partnerRules}
                        />
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="borderoNumber"
                                label={getI18n("freight-bordero-number", "Bordero Number", intl)}
                            >
                                <Input
                                    allowClear={true}
                                    placeholder={getI18n("freight-bordero-number-placeholder", "Enter Bordero Number", intl)}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="dateFrom"
                                label={getI18n("freight-beleganforderung-valid-from", "Bordero Date From", intl)}
                                dependencies={["dateUntil"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value?: dayjs.Dayjs) {
                                            if (!value) {
                                                return Promise.resolve();
                                            }

                                            const normalizedValue = value.startOf("day");
                                            const dateUntil = getFieldValue("dateUntil") as dayjs.Dayjs | undefined;

                                            if (normalizedValue.isBefore(minDate) || normalizedValue.isAfter(maxDate)) {
                                                return Promise.reject(new Error(getI18n(
                                                    "freight-date-from-range-error",
                                                    "Bordero Date From must be between today minus 1095 days and today.",
                                                    intl,
                                                )));
                                            }

                                            if (dateUntil && normalizedValue.isAfter(dateUntil.startOf("day"))) {
                                                return Promise.reject(new Error(getI18n(
                                                    "freight-date-from-after-until-error",
                                                    "Bordero Date From cannot be later than Bordero Date Until.",
                                                    intl,
                                                )));
                                            }

                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker
                                    className={moduleStyles.fullWidthControl}
                                    format="YYYY-MM-DD"
                                    placeholder={getI18n("freight-beleganforderung-valid-from-placeholder", "yyyy-MM-dd", intl)}
                                    locale={getRangePickerLocalization(intl, selectedLanguage)}
                                    disabledDate={isFreightDateOutOfRange}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="dateUntil"
                                label={getI18n("freight-beleganforderung-valid-until", "Bordero Date Until", intl)}
                                dependencies={["dateFrom"]}
                                rules={[
                                    ({getFieldValue}) => ({
                                        validator(_, value?: dayjs.Dayjs) {
                                            if (!getFieldValue("dateFrom") || value) {
                                                const dateFrom = getFieldValue("dateFrom") as dayjs.Dayjs | undefined;

                                                if (!value) {
                                                    return Promise.resolve();
                                                }

                                                const normalizedValue = value.startOf("day");

                                                if (normalizedValue.isBefore(minDate) || normalizedValue.isAfter(maxDate)) {
                                                    return Promise.reject(new Error(getI18n(
                                                        "freight-date-until-range-error",
                                                        "Bordero Date Until must be between today minus 1095 days and today.",
                                                        intl,
                                                    )));
                                                }

                                                if (dateFrom && normalizedValue.isBefore(dateFrom.startOf("day"))) {
                                                    return Promise.reject(new Error(getI18n(
                                                        "freight-date-until-before-from-error",
                                                        "Bordero Date Until cannot be earlier than Bordero Date From.",
                                                        intl,
                                                    )));
                                                }

                                                return Promise.resolve();
                                            }

                                            return Promise.reject(new Error(getI18n("freight-date-to-required", "Date until is required when Date from is selected", intl)));
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker
                                    className={moduleStyles.fullWidthControl}
                                    format="YYYY-MM-DD"
                                    placeholder={getI18n("freight-beleganforderung-valid-until-placeholder", "yyyy-MM-dd", intl)}
                                    locale={getRangePickerLocalization(intl, selectedLanguage)}
                                    disabledDate={isFreightDateOutOfRange}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                name="status"
                                label={getI18n("freight-document-request-status", "Status", intl)}
                            >
                                <Select
                                    allowClear={true}
                                    mode="multiple"
                                    placeholder={getI18n("freight-document-request-status-placeholder", "Type of status to be selected", intl)}
                                    options={STATUS_OPTIONS.map((status) => ({label: getStatusOptionLabel(status, intl), value: status}))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className={moduleStyles.filterActions}>
                        <Tooltip title={intl.formatMessage({ id: "info-portal-search", defaultMessage: "Search" })}>
                            <Button
                                style={{ marginRight: "10px" }}
                                data-testid="info-portal-search-button"
                                type="primary"
                                htmlType="submit"
                                icon={<SearchOutlined />}
                                loading={loading}
                                size="large"
                            />
                        </Tooltip>

                        <Tooltip title={intl.formatMessage({ id: "do-clear", defaultMessage: "Clear" })}>
                            <Button
                                data-testid="info-portal-clear"
                                className={styles.simpleFlipRefresh}
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={onReset}
                                size="large"
                            />
                        </Tooltip>
                    </div>
                </Form>
            }
            resultHeader={
                <div className={moduleStyles.freightResultMeta}>
                    <span className={moduleStyles.freightResultCount}>
                        {getI18n("freight-beleganforderung-results", "Matching Bordero Rows", intl)}
                    </span>
                    <Space>
                        <Badge
                            className="site-badge-count-109"
                            count={rows.length}
                            style={{backgroundColor: "#52c41a"}}
                            title={"Number of Matching Rows"}
                            overflowCount={999999}
                        />
                    </Space>
                    <div className={moduleStyles.freightHintBox}>
                        {getI18n(
                            "freight-beleganforderung-upload-hint",
                            "Upload format: Partner+Bordero+Position (separators +, -, _ are accepted).",
                            intl,
                        )}
                    </div>
                </div>
            }
            toolbar={
                <FreightGridToolbar
                    intl={intl}
                    menuItems={menuItems}
                    onExport={onExport}
                    onResetView={onResetView}
                    extraActions={
                        <div className={moduleStyles.documentsToolbarCol}>
                            <Tooltip title={getI18n("freight-beleganforderung-upload-selected", "Bulk Upload", intl)}>
                                <Upload
                                    showUploadList={false}
                                    multiple={true}
                                    beforeUpload={(file) => {
                                        pendingBulkUploadFilesRef.current.push(file);

                                        if (bulkUploadTimerRef.current) {
                                            cancelAnimationFrame(bulkUploadTimerRef.current);
                                        }

                                        // Batch files selected in one dialog interaction into a single upload run.
                                        bulkUploadTimerRef.current = requestAnimationFrame(() => {
                                            const nextFiles = pendingBulkUploadFilesRef.current;
                                            pendingBulkUploadFilesRef.current = [];
                                            bulkUploadTimerRef.current = null;

                                            if (nextFiles.length > 0) {
                                                onUploadSelected(nextFiles).catch(() => undefined);
                                            }
                                        });

                                        return false;
                                    }}
                                >
                                    <Button
                                        data-testid="info-portal-upload-selected"
                                        icon={<UploadOutlined />}
                                        size="middle"
                                        loading={isBulkUploading}
                                    >
                                        {getI18n("freight-beleganforderung-upload-selected", "Bulk Upload", intl)}
                                    </Button>
                                </Upload>
                            </Tooltip>
                        </div>
                    }
                />
            }
            grid={
                <div className={`ag-theme-balham ${moduleStyles.documentRequestGrid}`}>
                    <AgGridReact
                        defaultColDef={{
                            sortable: true,
                            resizable: true,
                            filter: true,
                        }}
                        columnDefs={columnDefs}
                        rowData={rows}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        pagination={true}
                        paginationPageSize={20}
                        localeText={getAgGridLocalization(intl)}
                        enableBrowserTooltips={true}
                        onGridReady={hookOnGridReady}
                        data-testid="beleganforderung-grid"
                    />
                </div>
            }
        />
    );
};

export default BeleganforderungComponent;